import { Component, Input, inject } from '@angular/core';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { LocalizationService } from '../../core/localization/localization.service';

export interface AinDataColumn<T> {
  field: keyof T & string;
  header: string;
  sortable?: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-data-table',
  imports: [TableModule, ButtonModule],
  template: `
    <div class="ain-table-toolbar">
      <span>{{ t(title) }}</span>
      <button pButton type="button" severity="secondary" size="small" [label]="t('common.export')" icon="pi pi-download"></button>
    </div>
    <p-table
      [value]="items"
      [paginator]="true"
      [rows]="rows"
      [rowsPerPageOptions]="[10, 25, 50]"
      [globalFilterFields]="globalFilterFields"
      [tableStyle]="{ 'min-width': '48rem' }">
      <ng-template pTemplate="header">
        <tr>
          @for (column of columns; track column.field) {
            <th [pSortableColumn]="column.sortable ? column.field : undefined">
              {{ t(column.header) }}
              @if (column.sortable) {
                <p-sortIcon [field]="column.field" />
              }
            </th>
          }
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          @for (column of columns; track column.field) {
            <td>{{ row[column.field] }}</td>
          }
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td [attr.colspan]="columns.length">{{ t('common.noRecords') }}</td>
        </tr>
      </ng-template>
    </p-table>
  `,
  styles: [`
    .ain-table-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
    .ain-table-toolbar span { font-weight: 700; }
  `]
})
export class AinDataTableComponent<T extends object> {
  private readonly localizationService = inject(LocalizationService);

  @Input() title = 'Records';
  @Input() items: T[] = [];
  @Input() columns: AinDataColumn<T>[] = [];
  @Input() rows = 10;

  get globalFilterFields(): string[] {
    return this.columns.map(column => column.field);
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
